/**
 * Which slice of the strike ladder is on screen.
 *
 * Extracted from the Terminal because the arithmetic is the kind that is
 * wrong by one and looks right: the window has to stay centred on ATM, clamp
 * at both ends of the ladder, and report what it hid so the "load more" rows
 * know whether to appear at all.
 */

export interface ChainWindow<T> {
  visible: T[]
  /** Rows hidden before the window — LOWER strikes, above it on screen. */
  hiddenAbove: number
  /** Rows hidden after the window — HIGHER strikes, below it on screen. */
  hiddenBelow: number
}

/**
 * The index of the row nearest `atm`.
 *
 * Nearest rather than exact: a ladder does not always carry the strike the
 * spot rounds to — an illiquid expiry can skip one — and falling back to
 * "show everything" because 22,600 is absent would be a worse answer than
 * centring on 22,550.
 */
export function nearestStrikeIndex(strikes: readonly number[], atm: number | null): number {
  if (atm === null || strikes.length === 0) return -1
  let best = -1
  let bestDistance = Infinity
  for (let i = 0; i < strikes.length; i += 1) {
    const d = Math.abs(strikes[i]! - atm)
    // Strictly less, so the FIRST of two equidistant strikes wins and the
    // window is stable rather than flipping between them as the spot moves.
    if (d < bestDistance) {
      bestDistance = d
      best = i
    }
  }
  return best
}

/**
 * Slice the ladder to `half` strikes either side of `centre`, widened by what
 * the user has asked for.
 *
 * A centre of -1 means there is no ATM yet — no spot has arrived — and the
 * whole ladder is returned. Hiding rows around a guessed centre would hide
 * the part someone is looking at.
 */
export function chainWindow<T>(
  rows: readonly T[],
  centre: number,
  half: number,
  extraAbove = 0,
  extraBelow = 0,
): ChainWindow<T> {
  if (centre < 0 || rows.length === 0) {
    return { visible: [...rows], hiddenAbove: 0, hiddenBelow: 0 }
  }
  const from = Math.max(0, centre - half - extraAbove)
  const to = Math.min(rows.length, centre + half + extraBelow + 1)
  return {
    visible: rows.slice(from, to),
    hiddenAbove: from,
    hiddenBelow: rows.length - to,
  }
}

/** How far the window reaches beyond the default, on each side. */
export interface WindowExtras {
  /** Extra rows at LOWER strikes, which sit ABOVE on screen. */
  readonly above: number
  /** Extra rows at HIGHER strikes, which sit BELOW on screen. */
  readonly below: number
}

/**
 * How many strikes the spot may drift before the window reaches after it.
 *
 * Three, because that is ~150 NIFTY points and a move that large inside one
 * session is the exception rather than the tick-by-tick norm. Below three
 * the deadband stops being a deadband: the ATM flickers between two strikes
 * on either side of the half-step boundary all day.
 */
export const DRIFT_DEADBAND_STRIKES = 3

/**
 * Reach further when the spot has walked out of the window — ROADMAP 4.19.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE WINDOW GROWS. IT NEVER SLIDES, AND IT NEVER DROPS A ROW.
 *
 * The chain used to be re-sliced around the live ATM on every tick that
 * crossed a half-strike boundary. Each re-slice added one strike at one end
 * and REMOVED one at the other, so every row on screen moved by a row
 * height — repeatedly, at feed rate.
 *
 * Reported by the trader who found it: "suppose a trader keeping the mouse
 * on a buy btn of a specific strike and the strike got changed in the
 * position and now something else has bought". That is the whole problem.
 * This screen fires on the click with no confirmation (see `placeOrder.ts`),
 * so a row that moves between the eye and the finger is not a cosmetic
 * annoyance — it is the wrong contract, at market, immediately.
 *
 * So the centre is ANCHORED where it was when the ladder loaded, and drift
 * is answered by widening rather than by re-centring. A row that is on
 * screen stays at the index it was at, for as long as this ladder is up.
 * The cost is a window that is off-centre by up to the deadband, and then
 * lopsided after a trend — which is the correct trade: nobody was ever hurt
 * by a chain showing four more strikes on one side.
 *
 * `Math.max` and never a decrease: the window is not allowed to shrink back
 * when the spot returns, because shrinking is removing rows, which is the
 * thing being fixed.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function widenForDrift(
  anchor: number,
  liveIndex: number,
  extras: WindowExtras,
  deadband: number = DRIFT_DEADBAND_STRIKES,
): WindowExtras {
  // No live ATM to compare against — before the first tick, or on a ladder
  // whose strikes have not arrived. Nothing is known, so nothing moves.
  if (liveIndex < 0 || anchor < 0) return extras

  const drift = liveIndex - anchor
  if (drift > deadband) return { above: extras.above, below: Math.max(extras.below, drift) }
  if (drift < -deadband) return { above: Math.max(extras.above, -drift), below: extras.below }
  return extras
}

/**
 * Reach far enough to cover the strikes a chosen strategy needs — 9.21.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE WINDOW IS NOT JUST WHAT IS DRAWN. IT IS WHAT EXISTS.
 *
 * `previewRows` and `chainTokens` are both derived from the visible window,
 * deliberately — the picker must never price a strike the table is not
 * showing. The consequence only appears once a strategy reaches past the
 * window: those strikes are not rendered, so they are not subscribed, so
 * they have no quote, so `resolveLegs` cannot find them.
 *
 * Reported on 2026-11-03 with a 30-leg strategy spanning ATM-10 to ATM+20:
 *
 *   "4 of 30 leg(s) could not be resolved. Nothing was built."
 *
 * The default half-width is 16, so the window stopped at ATM+16 and the top
 * four strikes — ATM+17 to ATM+20 — did not exist as far as the resolver was
 * concerned. Pressing "Show 10 higher strikes" fixed it by hand, which is
 * the whole tell: nothing was wrong with the catalogue or the feed, only
 * with how far the window reached.
 *
 * WIDENING RATHER THAN SPECIAL-CASING keeps one rule in one place. The
 * strikes become visible, subscribed, priced and resolvable together, and
 * the trader can SEE every strike their strategy is about to trade instead
 * of taking the basket on trust.
 *
 * IT ONLY EVER GROWS, like `widenForDrift` beside it: a strategy that needs
 * less than the window already shows changes nothing, and deselecting one
 * does not snatch rows back from under the cursor.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function widenForStrategy(
  extras: WindowExtras,
  /** `ATM_OFFSET` steps, signed: negative is a lower strike. */
  steps: readonly number[],
  half: number,
): WindowExtras {
  let { above, below } = extras
  for (const step of steps) {
    if (!Number.isFinite(step)) continue
    // `above` is LOWER strikes and `below` is HIGHER ones — see `WindowExtras`.
    if (step > 0) below = Math.max(below, Math.ceil(step) - half)
    else if (step < 0) above = Math.max(above, Math.ceil(-step) - half)
  }
  return { above, below }
}
